import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CheckCircle2, BarChart3, Truck, TrendingUp, FileText, Zap, Loader2, AlertCircle, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function codePreviewText(p: any, t: TFunction): string {
  if (p.mode === "trial") {
    const duration = p.trialDays && p.trialDays > 0
      ? t("auth:subscribe.trialDurationDays", { count: p.trialDays })
      : t("auth:subscribe.trialDurationOneYear");
    return t("auth:subscribe.trialPreview", { duration });
  }
  if (p.mode === "discount") {
    const value = p.discountType === "percent"
      ? t("auth:subscribe.discountPercent", { value: parseFloat(p.discountValue) })
      : t("auth:subscribe.discountAmount", { value: parseFloat(p.discountValue).toFixed(2) });
    return p.durationMonths
      ? t("auth:subscribe.discountForMonths", { value, count: p.durationMonths })
      : t("auth:subscribe.discountEveryMonth", { value });
  }
  if (p.mode === "fixed_price") {
    const amt = t("auth:subscribe.fixedPriceAmt", { amount: parseFloat(p.fixedPriceAmount).toFixed(2) });
    return p.durationMonths
      ? t("auth:subscribe.fixedPriceForMonths", { amt, count: p.durationMonths })
      : t("auth:subscribe.fixedPricePlain", { amt });
  }
  return t("auth:subscribe.codeApplied");
}

function getFeatures(t: TFunction) {
  return [
    { icon: BarChart3, text: t("auth:subscribe.featureMultiLender") },
    { icon: FileText, text: t("auth:subscribe.featurePdfUpload") },
    { icon: Truck, text: t("auth:subscribe.featureInventory") },
    { icon: TrendingUp, text: t("auth:subscribe.featurePerformance") },
    { icon: Zap, text: t("auth:subscribe.featureApr") },
    { icon: CheckCircle2, text: t("auth:subscribe.featureSecure") },
  ];
}

export default function SubscribePage() {
  const [, navigate] = useLocation();
  const { t } = useTranslation(["auth", "common"]);
  const qc = useQueryClient();
  const { toast } = useToast();
  const FEATURES = getFeatures(t);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const isSuccess = params.get("success") === "true";
  const isCanceled = params.get("canceled") === "true";

  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [code, setCode] = useState("");
  const [codePreview, setCodePreview] = useState<any>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [checkingCode, setCheckingCode] = useState(false);

  const { data: user } = useQuery<any>({
    queryKey: ["/api/auth/me"],
    retry: false,
  });

  const { data: pricing } = useQuery<any>({
    queryKey: ["/api/billing/platform-pricing"],
    staleTime: 60_000,
  });

  const validateCode = async (): Promise<any | null> => {
    setCheckingCode(true);
    setCodeError(null);
    try {
      const resp = await apiRequest("POST", "/api/billing/validate-code", { code: code.trim() });
      const preview = await resp.json();
      setCodePreview(preview);
      return preview;
    } catch (err: any) {
      setCodePreview(null);
      setCodeError(err.message?.replace(/^\d+:\s*/, "").replace(/^\{.*"message":"(.*)"\}$/, "$1") || t("auth:subscribe.invalidCode"));
      return null;
    } finally {
      setCheckingCode(false);
    }
  };

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      // Auto-apply: if a code is typed but not yet validated, validate it now.
      // Valid → include it; invalid → block checkout so the user sees the error.
      let preview = codePreview;
      if (code.trim() && !preview) {
        preview = await validateCode();
        if (!preview) {
          throw new Error(t("auth:subscribe.invalidCode"));
        }
      }
      const body = preview && code.trim() ? { code: code.trim() } : {};
      const resp = await apiRequest("POST", "/api/billing/create-checkout", body);
      return resp.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (err: any) => {
      toast({ title: t("common:error"), description: err.message || t("auth:subscribe.toast.failedCheckout"), variant: "destructive" });
    },
  });

  useEffect(() => {
    if (isSuccess && sessionId && !verifying && !verified) {
      setVerifying(true);
      apiRequest("POST", "/api/billing/verify-session", { session_id: sessionId })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            setVerified(true);
            // Full page load (not SPA navigation): guarantees the app boots
            // with the fresh subscription status instead of racing a stale
            // cached auth state, which could leave a blank screen.
            setTimeout(() => window.location.assign("/"), 1800);
          } else {
            setVerifyFailed(true);
          }
        })
        .catch(() => {
          setVerifyFailed(true);
          toast({ title: t("auth:subscribe.toast.activationIssueTitle"), description: t("auth:subscribe.toast.activationIssueDesc"), variant: "destructive" });
        })
        .finally(() => setVerifying(false));
    }
  }, [isSuccess, sessionId]);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/logout");
    },
    onSuccess: () => {
      qc.clear();
      navigate("/login");
    },
  });

  if (isSuccess && !verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md text-center p-8">
          <div className="flex flex-col items-center gap-4">
            {verifyFailed ? (
              <>
                <h2 className="text-xl font-semibold">{t("auth:subscribe.toast.activationIssueTitle")}</h2>
                <p className="text-muted-foreground text-sm">{t("auth:subscribe.toast.activationIssueDesc")}</p>
                <Button onClick={() => window.location.assign("/")} data-testid="button-activation-continue">
                  {t("common:continue", "Continue")}
                </Button>
              </>
            ) : (
              <>
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <h2 className="text-xl font-semibold">{t("auth:subscribe.activatingTitle")}</h2>
                <p className="text-muted-foreground text-sm">{t("auth:subscribe.activatingDesc")}</p>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md text-center p-8">
          <div className="flex flex-col items-center gap-4">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
            <h2 className="text-xl font-semibold">{t("auth:subscribe.allSetTitle")}</h2>
            <p className="text-muted-foreground text-sm">{t("auth:subscribe.allSetDesc")}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b py-4 px-6 flex items-center justify-between bg-background sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-bold text-lg tracking-tight">DealerMind</span>
        </div>
        {user && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{t("auth:subscribe.signedInAs")} <span className="font-medium">{user.username}</span></span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="button-sign-out"
              onClick={() => logoutMutation.mutate()}
            >
              {t("auth:subscribe.signOut")}
            </Button>
          </div>
        )}
      </header>

      <main className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-lg">
          {isCanceled && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t("auth:subscribe.canceledAlert")}
              </p>
            </div>
          )}

          <Card className="shadow-lg">
            <CardHeader className="text-center pb-2">
              <Badge variant="secondary" className="w-fit mx-auto mb-3" data-testid="badge-plan">
                {t("auth:subscribe.planBadge")}
              </Badge>
              <CardTitle className="text-3xl font-bold" data-testid="text-price">
                {pricing ? `$${pricing.basePrice}` : "—"}
                <span className="text-lg font-normal text-muted-foreground">{t("auth:subscribe.perMonth")}</span>
              </CardTitle>
              <CardDescription className="text-base mt-1">
                {t("auth:subscribe.planDesc")}
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-4">
              <ul className="space-y-3 mb-8">
                {FEATURES.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">{text}</span>
                  </li>
                ))}
              </ul>

              <div className="mb-6 max-w-sm mx-auto">
                <p className="text-xs text-muted-foreground mb-1.5 flex items-center justify-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> {t("auth:subscribe.haveCode")}
                </p>
                <div className="flex gap-2 justify-center">
                  <Input
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase());
                      setCodePreview(null);
                      setCodeError(null);
                    }}
                    placeholder={t("auth:subscribe.codePlaceholder")}
                    className="h-9 text-sm font-mono uppercase"
                    data-testid="input-signup-code"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    disabled={!code.trim() || checkingCode}
                    data-testid="button-apply-code"
                    onClick={() => void validateCode()}
                  >
                    {checkingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth:subscribe.apply")}
                  </Button>
                </div>
                {codePreview && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1.5 flex items-center justify-center gap-1" data-testid="text-code-preview">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {codePreviewText(codePreview, t)}
                  </p>
                )}
                {codeError && (
                  <p className="text-xs text-destructive mt-1.5 text-center" data-testid="text-code-error">{codeError}</p>
                )}
              </div>

              <Button
                className="w-full h-11 text-base font-semibold"
                data-testid="button-subscribe"
                onClick={() => checkoutMutation.mutate()}
                disabled={checkoutMutation.isPending}
              >
                {checkoutMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("auth:subscribe.preparingCheckout")}
                  </>
                ) : codePreview?.mode === "trial" ? (
                  t("auth:subscribe.startFreeTrial")
                ) : (
                  pricing ? t("auth:subscribe.subscribeNowPrice", { price: pricing.basePrice }) : t("auth:subscribe.subscribeNow")
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground mt-4">
                {t("auth:subscribe.secureCheckout")}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
